import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";

export const waNumber = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  return digits.length === 7 ? `1441${digits}` : digits.length === 10 ? `1${digits}` : digits;
};

const ua = () => (typeof navigator !== "undefined" ? navigator.userAgent : "");
export const isAndroid = () => /Android/i.test(ua());
export const isIOS = () => /iPhone|iPad|iPod/i.test(ua());

let desktopTarget = "web";
export const setWaDesktopTarget = (t) => { desktopTarget = t === "app" ? "app" : "web"; };

// Always target WhatsApp *Business*. Never fall back to wa.me on phones — that opens personal WhatsApp.
const W4B_PLAY = "https://play.google.com/store/apps/details?id=com.whatsapp.w4b";
const qs = (num, text) => `${num ? `phone=${num}&` : ""}text=${encodeURIComponent(text || "")}`;
export const waBusinessHref = (num, text) => {
  if (isAndroid()) return `intent://send?${qs(num, text)}#Intent;scheme=whatsapp;package=com.whatsapp.w4b;S.browser_fallback_url=${encodeURIComponent(W4B_PLAY)};end`;
  if (isIOS()) return `whatsapp-business://send?${qs(num, text)}`;
  if (desktopTarget === "app") return `whatsapp://send?${qs(num, text)}`;
  return `https://web.whatsapp.com/send?${qs(num, text)}`;
};
const waWebHref = (num, text) => `https://web.whatsapp.com/send?${qs(num, text)}`;
// Second attempt: whatsapp:// is also registered by WhatsApp Business, so with only Business installed it opens Business.
const waSchemeHref = (num, text) => `whatsapp://send?${qs(num, text)}`;

const copyFallback = async (text) => {
  try { await navigator.clipboard?.writeText(text || ""); } catch { /* ignore */ }
  toast.error("WhatsApp Business didn't open. Message copied — paste it in WhatsApp Business.", { duration: 6000 });
};

const tryOpen = (href, ms) => new Promise((resolve) => {
  let hidden = false;
  const onHide = () => { hidden = true; };
  window.addEventListener("pagehide", onHide, { once: true });
  document.addEventListener("visibilitychange", onHide, { once: true });
  window.addEventListener("blur", onHide, { once: true });
  setTimeout(() => resolve(hidden || document.visibilityState !== "visible"), ms);
  window.location.href = href;
});

export const openWaBusiness = async (num, text) => {
  if (isIOS() || isAndroid()) {
    if (await tryOpen(waBusinessHref(num, text), 1600)) return;
    if (await tryOpen(waSchemeHref(num, text), 1400)) return;
    copyFallback(text);
    return;
  }
  if (desktopTarget === "app") {
    // Hand off to WhatsApp Desktop; if nothing claimed the scheme, fall back to WhatsApp Web (never a wa.me landing page).
    if (await tryOpen(waBusinessHref(num, text), 1500)) return;
    window.open(waWebHref(num, text), "_blank", "noopener");
    return;
  }
  window.open(waBusinessHref(num, text), "_blank", "noopener");
};

export const useWaSettings = () => {
  const { data } = useQuery({ queryKey: ["wa-template"], queryFn: async () => (await api.get("/admin/wa-template")).data, staleTime: 60000 });
  useEffect(() => { if (data?.desktop_target) setWaDesktopTarget(data.desktop_target); }, [data?.desktop_target]);
  return data;
};

export const useBusinessContact = () => {
  const { data } = useQuery({ queryKey: ["public-contact"], queryFn: async () => (await api.get("/public/contact")).data, staleTime: 300000 });
  useEffect(() => { if (data?.desktop_target) setWaDesktopTarget(data.desktop_target); }, [data?.desktop_target]);
  return data || { whatsapp_display: "441-704-6671", whatsapp_number: "14417046671" };
};

export const WaBusinessLink = ({ num, text, children, onClick, ...rest }) => (
  <a href={waBusinessHref(num, text)} onClick={(e) => { e.preventDefault(); onClick?.(e); openWaBusiness(num, text); }} rel="noreferrer" {...rest}>{children}</a>
);
